import type { PerformanceResourceItem } from '@/app/lib/api/types-api'
import { formatEventDate } from '@/app/lib/dates'
import { filterVisiblePromotions, parsePriceField, resolveEventPrice } from '@/app/lib/pricing'
import { resolvePurchaseState } from '@/app/lib/purchaseState'
import type { PurchaseState } from '@/app/lib/types'
import { route, routes } from '@/app/lib/routing'

import { buildGoogleCalendarUrl } from './buildGoogleCalendarUrl'
import { buildPerformanceTitle } from './buildPerformanceTitle'
import { hasSpecialConditions } from './hasSpecialConditions'
import { resolveArtistLinks } from './resolveArtistLinks'
import type { ResolvedArtistLinks } from './resolveArtistLinks'
import type { PerformanceViewOptions } from './types'

/** Separador entre recinto y provincia en la cabecera (FR-011). */
const VENUE_SEPARATOR = ' · '

/** `nicename` del `extras[]` que trae la hora del evento (FR-010). */
const SHOW_TIMES = 'show_times'

export interface PerformanceHeroViewModel {
  readonly title: string
  /** Cartel 4:5 (FR-006). */
  readonly posterSrc: string | null
  /** Fondo difuminado — panorámica, o el cartel si no la hay (FR-007). Decorativo. */
  readonly backdropSrc: string | null
  readonly dateLabel: string
  /** Texto literal de `extras['show_times']`; `null` si no llega (FR-010). */
  readonly timeLabel: string | null
  /**
   * Antetítulo sobre el título (p. ej. «Conciertos»), fiel a `ProductHero.kicker`
   * de `eci-firefly-uikit`. `genres[0].name` tal cual lo manda el backend; `null`
   * si no llega — nunca se rellena con el slug de categoría (ese fallback es
   * cosa del paquete SEO, que sí necesita un valor siempre presente).
   */
  readonly categoryLabel: string | null
  /**
   * `Recinto · Provincia`, sin fragmentos vacíos ni separadores sueltos. Es la
   * parte **enlazable** de la fila de recinto: la sala viaja aparte en
   * `roomLabel` porque el diseño la deja como texto plano tras un separador —
   * no forma parte del destino del enlace.
   */
  readonly venueLabel: string | null
  /** Sala dentro del recinto, sin enlazar; `null` si no llega (el caso normal). */
  readonly roomLabel: string | null
  /** Destino de la página de recinto; `null` ⇒ el texto se pinta sin enlazar. */
  readonly venueHref: string | null
  readonly price: ReturnType<typeof resolveEventPrice>
  readonly purchaseState: PurchaseState
  /**
   * `true` ⇒ el rail del hero suma la etiqueta amarilla de aviso. Es el mismo
   * interruptor que enciende el banner de condiciones especiales, para que los
   * dos no puedan discrepar.
   */
  readonly hasSpecialConditions: boolean
  readonly artists: ResolvedArtistLinks
  /** Enlace a landing relacionada, al mismo nivel que el de artista (FR-020). */
  readonly landingLink: { readonly href: string; readonly label: string } | null
  /**
   * Recordatorio de la apertura de venta en Google Calendar. Solo tiene valor en
   * el estado `upcoming`: en el resto no hay nada que recordar, así que la
   * condición se resuelve aquí una vez en lugar de repetirse en cada consumidor.
   */
  readonly calendarUrl: string | null
  readonly purchaseUrl: string | null
  readonly purchaseText: string | null
}

/** Devuelve el texto de un `extras[]` por `nicename`, o `null` si no está. */
function findExtra(extras: PerformanceResourceItem['extras'], nicename: string): string | null {
  const match = extras?.find((extra) => extra.nicename === nicename)
  const text = match?.text?.trim()
  return text ? text : null
}

/**
 * Une recinto y provincia descartando los vacíos.
 *
 * Descartar antes de unir —y no unir y luego limpiar— es lo que evita el
 * separador suelto cuando falta uno de los dos.
 *
 * La sala **ya no entra aquí**: el diseño enlaza solo «Recinto · Provincia» a
 * la página de recinto y deja la sala como texto plano detrás, así que va por
 * `roomLabel`. Meterla en esta cadena haría que el enlace prometiera llevar a
 * una sala concreta, cosa que la ruta de recinto no hace.
 */
function buildVenueLabel(resource: PerformanceResourceItem): string | null {
  const parts = [resource.venue?.name, resource.venue?.province]
    .map((part) => part?.trim())
    .filter((part): part is string => Boolean(part))

  return parts.length ? parts.join(VENUE_SEPARATOR) : null
}

/**
 * Destino de la página de detalle de recinto.
 *
 * Se construye con **`venue.url`** (nicename + sufijo de id, p. ej.
 * `nuevo-teatro-alcala-SP01`), NO con `venue.nicename`: es lo que espera la
 * ruta ya existente `recintos/[location]/[url]/` (specs/041-pagina-recinto).
 * Con `nicename` a secas el enlace daría 404 — decisión registrada en
 * spec.md, Clarifications.
 */
/**
 * `landing_url` llega relativa a `entradas/` (p. ej. `festivales/SanSanFestival/`).
 * Un `href` relativo se resolvería contra la URL de la ficha; se normaliza a
 * absoluto bajo `/entradas/`. Si ya trae el prefijo o es externa, no se toca.
 */
function buildLandingHref(url: string): string {
  return /^\/?entradas(\/|$)/.test(url) ? route(url.replace(/^\/?entradas/, '')) : route(url)
}

function buildVenueHref(resource: PerformanceResourceItem): string | null {
  const province = resource.venue?.province_nicename?.trim()
  const url = resource.venue?.url?.trim()
  return province && url ? routes.recinto(province, url) : null
}

/**
 * Modelo de vista de la cabecera de la ficha (data-model.md §2).
 *
 * **No implementa ninguna regla de negocio propia.** Precio, promoción visible,
 * fecha y estado de compra se delegan íntegramente en las utilidades ya
 * existentes (`resolveEventPrice`, `filterVisiblePromotions`, `formatEventDate`,
 * `resolvePurchaseState`), que son las mismas que alimentan las tarjetas de
 * cualquier listado. Es lo que garantiza que la ficha y la tarjeta del mismo
 * evento nunca discrepen (spec.md FR-013 y SC-005). Si alguna vez hace falta
 * reimplementar aquí una de esas reglas, es señal de que la delegación está mal
 * planteada.
 *
 * Por el mismo motivo, la etiqueta de promoción del rail del hero sale de
 * `price.tag` —lo que ya calcula `resolveEventPrice` y lo que pintan las
 * tarjetas— y no de una promoción especial resuelta aquí aparte.
 *
 * `dates_type` no existe en el recurso de la ficha (sí en `/data`): se pasa
 * `null`, con lo que `formatEventDate` cae en su rama de rango — "Del X al Y"
 * con `start_at` + `end_at`, "Desde el X" con solo `start_at`. Es el mismo
 * comportamiento que ya tienen los eventos de `/resource/batch`.
 */
export function toPerformanceHeroViewModel(
  resource: PerformanceResourceItem,
  options: PerformanceViewOptions = {},
): PerformanceHeroViewModel {
  const visiblePromotions = filterVisiblePromotions(resource.promotions ?? [])

  const landingUrl = resource.landing_url?.trim()
  const landingText = resource.landing_text?.trim()

  const purchaseState = resolvePurchaseState({
    isCancelled: resource.is_cancelled,
    purchaseText: resource.purchase_text,
    disablePurchase: resource.disable_purchase ?? false,
    soldOut: resource.sold_out,
    saleEnd: resource.sale_end,
    saleStart: resource.sale_start,
  })

  return {
    title: buildPerformanceTitle(resource.title, resource.venue?.province),
    posterSrc: resource.image_vertical?.trim() || null,
    backdropSrc: resource.image_panoramic?.trim() || resource.image_vertical?.trim() || null,
    dateLabel: formatEventDate({
      start: resource.start_at,
      end: resource.end_at,
      datesType: null,
      dateNotVisible: resource.date_not_visible,
    }),
    timeLabel: findExtra(resource.extras, SHOW_TIMES),
    categoryLabel: resource.genres?.[0]?.name?.trim() || null,
    venueLabel: buildVenueLabel(resource),
    roomLabel: resource.room?.trim() || null,
    venueHref: buildVenueHref(resource),
    price: resolveEventPrice({
      minPrice: resource.min_price,
      originalPrice: resource.original_price,
      visiblePromotions,
      hasAppPromotions: resource.has_app_promotions ?? false,
      salePrice: parsePriceField(resource.sale_price),
      discountPercentage: parsePriceField(resource.discount),
    }),
    purchaseState,
    hasSpecialConditions: hasSpecialConditions(resource, options),
    artists: resolveArtistLinks(resource),
    landingLink: landingUrl && landingText ? { href: buildLandingHref(landingUrl), label: landingText } : null,
    calendarUrl: purchaseState.kind === 'upcoming' ? buildGoogleCalendarUrl(resource) : null,
    purchaseUrl: resource.purchase_url,
    purchaseText: resource.purchase_text,
  }
}
